import { applyListPatch, applyMainOddsToDetailMarkets, failCloseSportsnewDetailMarkets, filterSportsnewListRecoveryFields, normalizeSportsnewListRecovery, reconcileSportsnewListRecoveryRows, resolveMainOddsUpdatesFromListPatch, trackSportsnewListRecoveryFields } from '../_helpers/sportsnewListPatch';
import { applySportsnewListRealtimeSince, sportsnewFixtureIdsFromListPatch } from '../_helpers/sportsnewDetailRealtime';
import { listSubscriptionChanged, normalizedListSubscription } from '../_helpers/sportsnewContextConfig';
import { markWsPatchReceived, measureWsPatchToRender } from '../_helpers/sportsnewPerf';
import { resolveOperatorVisibilityPatch, stripOperatorVisibilityRemovals } from '../_helpers/sportsnewOperatorVisibility';
import { sportsnewListPatchHasMembershipTransition, sportsnewMembershipPatchProvidesReopenAuthority, sportsnewMembershipPatchRequiresFailClosedHold } from '../_helpers/sportsnewMembershipRevision';
import { useCallback } from 'react';

// Owns list realtime patch within the existing split context.
// Provider refs retain account/list/detail authority; callers receive stable actions.
export const useSportsnewListRealtimePatch = ({
  frameDeliveryRef,
  SportsnewService, acceptDisplayPatch, acceptListSnapshot, armMembershipQuarantineReopen,
  clearActiveDetailIfSuspended, commitDetailMarkets, commitFixtures,
  detailFixtureIdRef, detailRealtimeLedgerRef, failCloseQuarantinedFixtures, fixturesRef,
  fixturesRequestSeqRef, flashMainOddsDiff, listFilterKeyRef, listRecoveryInFlightRef,
  listRecoveryLastRef, listSequenceRef, listSubscriptionRef, membershipFailClosedFixturesRef,
  modeRef, quarantineMembershipFixtures, recoverFixtureDetailSingleFlight, scheduleMembershipGenerationRefresh,
  sendAck, sessionOwnerRef, socketGenerationRef, updateDetailCacheForFixtures,
  updateOperatorSuspendedFixtureIds, listPresentationRef, listStreamRef, requestPresentationListRecovery,
}) => {
  const handleListPatch = useCallback(({ sequence, emittedAt, patch, streamEpoch }, { recoveryReplay = false, recoveryOwner = null } = {}) => {
      // Reject patches for a room we've since left. Confirmed via captured real
      // payloads that every patch carries its own `mode` (and `sport`). Without
      // this, a patch already in flight for the OLD mode when the user switches
      // tabs (e.g. International → Live → back to International) can arrive
      // just after the new tab's fresh REST fetch and, because list-patch
      // merging never prunes (see applyListPatch), permanently re-insert a
      // fixture that has since expired/started — it silently reappears with no
      // further REST fetch to remove it again.
      //
      // mode alone is not a strong enough identity for a multi-room subscription
      // model: page/league/game title/status changes can produce a brand-new
      // filterHash room under the SAME mode+sport (see the fetchFixtures
      // subscription-swap above), and a socket can be mid-transition between two
      // such rooms. So also reject by sport and filterHash against our own
      // active subscription, and by room if the backend ever adds one — all
      // defensive no-ops today for whichever field a given patch doesn't carry.
      const activeListSub = listSubscriptionRef.current;
      if (!activeListSub) return;
      if (patch?.room && activeListSub?.room && patch.room !== activeListSub.room) return;
      if (patch?.mode && patch.mode !== modeRef.current) return;
      if (patch?.scope && activeListSub?.scope && patch.scope !== activeListSub.scope) return;
      if (
        patch?.sport &&
        activeListSub?.sport &&
        activeListSub.sport !== 'all' &&
        patch.sport !== activeListSub.sport
      ) return;
      if (
        patch?.filterHash &&
        activeListSub?.filterHash &&
        patch.filterHash !== activeListSub.filterHash
      ) return;

      const presentation = listPresentationRef.current;
      const presentationActive = presentation?.requestKey === listFilterKeyRef.current
        && presentation.socketGeneration === socketGenerationRef.current;
      if (presentationActive && (typeof streamEpoch !== 'string'
        || streamEpoch !== presentation.cursor?.epoch)) {
        requestPresentationListRecovery(true);
        return false;
      }

      if (presentationActive && patch?.room && patch.room !== presentation.room) return false;
      // A public baseline is not ledger authority. Only its next confirmed list
      // event can change display availability without an individual Writer read.
      const presentationOrderConfirmed = !recoveryReplay && presentationActive
        && presentation.room === (activeListSub?.room || `sportsnew:list:${activeListSub?.mode}:${activeListSub?.filterHash}`)
        && Number.isSafeInteger(sequence) && Number.isSafeInteger(listSequenceRef.current)
        && sequence === listSequenceRef.current + 1;

      // Only mark/measure patches that actually pass identity — a rejected
      // patch is never rendered, so timing it would be meaningless (and would
      // accumulate marks that never get cleared by a matching measure).
      if (!recoveryReplay && Number.isSafeInteger(sequence)
        && listSequenceRef.current !== null && sequence <= listSequenceRef.current) {
        sendAck(patch?.room || 'sportsnew:list', sequence, emittedAt);
        return false;
      }
      if (!recoveryReplay) markWsPatchReceived('list', sequence);

      // The REST snapshot is authoritative for THIS page's composition —
      // applyListPatch (below) already refuses to insert a fixtureId the page
      // never showed, so it can never silently grow past the page size. A
      // fixture outside the current page is simply ignored here. Ordinary odds,
      // lock, needsRefetch and listDirty traffic never auto-refetches. The sole
      // exception is an explicit lifecycle/operator membership transition: it
      // schedules one coalesced list-generation refresh so the left-rail counts
      // cannot remain on the generation before the insert/remove.

      // Sequence gap → recovery (with 10s cooldown to prevent loop). Because
      // every branch above returns BEFORE this point for a patch belonging to a
      // room we've left, listSequenceRef is only ever read/written by patches
      // from the currently active room — i.e. it behaves as a per-room sequence
      // without needing a room-keyed map.
      if (!recoveryReplay && listSequenceRef.current !== null
        && Number.isSafeInteger(sequence) && sequence > listSequenceRef.current + 1) {
        if (presentationActive) requestPresentationListRecovery(true);
        const now = Date.now();
        const sub = listSubscriptionRef.current;
        if (!presentationActive && !listRecoveryInFlightRef.current && now - listRecoveryLastRef.current > 10000
          && sub && SportsnewService) {
          frameDeliveryRef?.current?.flush();
          const owner = {
            sub, since: listSequenceRef.current, sequence: fixturesRequestSeqRef.current,
            key: listFilterKeyRef.current, socketGeneration: socketGenerationRef.current,
            sessionOwner: sessionOwnerRef.current, realtimeRevision: detailRealtimeLedgerRef.current.revision,
            replayFence: { byFixture: new Map(), invalid: false },
          };
          listRecoveryInFlightRef.current = owner;
          listRecoveryLastRef.current = now;
          const params = {
            mode: sub.mode, scope: sub.scope, sport: sub.sport || undefined,
            league_id: sub.leagueId || undefined,
            game_title_id: sub.gameTitleId || undefined,
            tournament_id: sub.tournamentId || undefined,
            status: sub.status || undefined, featured: sub.featured,
            page: sub.page, limit: sub.limit, filter_hash: sub.filterHash, since: owner.since,
          };
          const isCurrent = () => listRecoveryInFlightRef.current === owner
            && !owner.replayFence.invalid
            && fixturesRequestSeqRef.current === owner.sequence
            && listFilterKeyRef.current === owner.key
            && socketGenerationRef.current === owner.socketGeneration
            && sessionOwnerRef.current === owner.sessionOwner
            && !listSubscriptionChanged(owner.sub, listSubscriptionRef.current);
          const room = sub.room || `sportsnew:list:${sub.mode}:${sub.filterHash}`;
          SportsnewService.recoverLists(params).then((res) => {
    frameDeliveryRef?.current?.flush();
            if (listSubscriptionRef.current !== sub) return;
            if (!isCurrent()) return;
            const recovered = normalizeSportsnewListRecovery(res, { room, since: owner.since });
            if (!recovered) return;
            if (recovered.kind === 'snapshot') {
              const snapshot = { ...recovered.snapshot, rows: reconcileSportsnewListRecoveryRows(
                recovered.snapshot.rows, owner.replayFence, recovered.sequence, fixturesRef.current,
              ) };
              const echoed = normalizedListSubscription({
                echoed: snapshot.subscription, params: { ...params, page: snapshot.page, limit: snapshot.limit },
                hash: snapshot.subscription.filterHash,
              });
              if (listSubscriptionChanged(sub, echoed)) return;
              const accepted = acceptListSnapshot(
                { ...res, httpStatus: res.httpStatus ?? 200, data: snapshot }, params, owner,
              );
              if (!accepted) return;
            } else if (recovered.kind === 'deltas') {
              // Reuse the event owner without recursively recovering, re-ACKing,
              // or creating flash timers. Per-room evidence rejects stale groups.
              for (const delta of recovered.deltas) {
                if (!isCurrent() || handleListPatch(delta, { recoveryReplay: true, recoveryOwner: owner }) !== true) return;
              }
            } else {
              if (recovered.subscription?.filterHash
                && recovered.subscription.filterHash !== sub.filterHash) return;
              const rows = applySportsnewListRealtimeSince(
                reconcileSportsnewListRecoveryRows(recovered.rows, owner.replayFence,
                  recovered.sequence ?? owner.since, fixturesRef.current),
                detailRealtimeLedgerRef.current, owner.realtimeRevision, fixturesRef.current,
                {requireWriterForUnlock:modeRef.current==='live',onAmbiguous:(id,fixtureId)=>recoverFixtureDetailSingleFlight(fixtureId,{snapshot:true,focusOddStateIds:[id]})},
              );
              commitFixtures(prev => failCloseQuarantinedFixtures(applyListPatch(prev, { fixtures: rows })));
            }
            if (Number.isSafeInteger(recovered.sequence)) {
              listSequenceRef.current = Math.max(listSequenceRef.current ?? 0, recovered.sequence);
            }
          }).catch(() => {}).finally(() => {
            if (listRecoveryInFlightRef.current === owner) listRecoveryInFlightRef.current = false;
          });
        }
      }

      if (!recoveryReplay && Number.isSafeInteger(sequence)) {
        listSequenceRef.current = Math.max(listSequenceRef.current ?? 0, sequence);
      }
      const controlIsCurrent = id => !recoveryReplay || sequence > (
        recoveryOwner?.replayFence?.byFixture.get(String(id))?.get('$membership') ?? -1
      );
      if (recoveryReplay) {
        patch = filterSportsnewListRecoveryFields(patch, recoveryOwner?.replayFence, sequence);
        if (Array.isArray(patch.ops)) patch = { ...patch, ops: patch.ops.filter(op =>
          (op?.op ?? op?.type) !== 'remove' || controlIsCurrent(op.fixtureId ?? op.id)) };
      }
      patch = acceptDisplayPatch(patch, 'list', {
        room: activeListSub?.room || `sportsnew:list:${activeListSub?.mode}:${activeListSub?.filterHash}`,
        connection: socketGenerationRef.current, epoch: streamEpoch, sequence,
      }, { presentationOrderConfirmed });
      if (!recoveryReplay && typeof streamEpoch === 'string') {
        const room = activeListSub?.room || `sportsnew:list:${activeListSub?.mode}:${activeListSub?.filterHash}`;
        const previous = listStreamRef.current;
        const sameStream = previous?.room === room && previous?.epoch === streamEpoch
          && previous?.socketGeneration === socketGenerationRef.current;
        listStreamRef.current = { room, epoch: streamEpoch, socketGeneration: socketGenerationRef.current,
          fields: sameStream ? previous.fields : { byFixture: new Map(), invalid: false } };
        trackSportsnewListRecoveryFields(listStreamRef.current.fields, patch, sequence);
      }
      let operatorVisibility = resolveOperatorVisibilityPatch(patch);
      if (recoveryReplay && operatorVisibility.action) {
        const fixtureIds = operatorVisibility.fixtureIds.filter(controlIsCurrent);
        operatorVisibility = { ...operatorVisibility, fixtureIds,
          action: fixtureIds.length ? operatorVisibility.action : null };
      }
      if (operatorVisibility.action === 'suspend') {
        updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, true);
        clearActiveDetailIfSuspended(operatorVisibility.fixtureIds);
      }
      const membershipFixtureIds = sportsnewFixtureIdsFromListPatch(patch);
      const controlFixtureIds = membershipFixtureIds.filter(controlIsCurrent);
      const membershipTransition = sportsnewListPatchHasMembershipTransition(patch)
        && (!recoveryReplay || controlFixtureIds.length > 0);
      const failClosedMembershipHold = membershipTransition
        && sportsnewMembershipPatchRequiresFailClosedHold(patch);
      if (!recoveryReplay) trackSportsnewListRecoveryFields(
        listRecoveryInFlightRef.current?.replayFence, patch, sequence,
        membershipTransition || operatorVisibility.action ? membershipFixtureIds : [],
      );
      if (failClosedMembershipHold) quarantineMembershipFixtures(controlFixtureIds);
      if (
        !failClosedMembershipHold
        && (
          operatorVisibility.action === 'resume'
          || sportsnewMembershipPatchProvidesReopenAuthority(patch)
        )
      ) {
        armMembershipQuarantineReopen([
          ...controlFixtureIds,
          ...operatorVisibility.fixtureIds,
        ]);
      }
      if (
        operatorVisibility.action !== null
        || membershipTransition
      ) {
        scheduleMembershipGenerationRefresh();
      }

      // Flash mainOdds price changes on list patches (read stale-safe from ref).
      // See resolveMainOddsUpdatesFromListPatch for exactly which patch shape is
      // authoritative — confirmed against a captured real payload.
      const mainOddsUpdates = resolveMainOddsUpdatesFromListPatch(patch, fixturesRef.current);
      if (!recoveryReplay) mainOddsUpdates.forEach(
        ({ oldMainOdds, newMainOdds }) => flashMainOddsDiff(oldMainOdds, newMainOdds)
      );
      updateDetailCacheForFixtures(
        membershipFixtureIds,
        (markets, fixtureId) => {
          const patched = applyMainOddsToDetailMarkets(patch, fixtureId, markets);
          return membershipFailClosedFixturesRef.current.has(String(fixtureId))
            ? failCloseSportsnewDetailMarkets(patched)
            : patched;
        },
      );

      // If this list-patch's fixture is the one currently open in the detail
      // panel, ALSO push its fresh mainOdds prices into detailMarkets — see
      // applyMainOddsToDetailMarkets for why this is necessary.
      commitDetailMarkets((prev) => {
        const patched = applyMainOddsToDetailMarkets(patch, detailFixtureIdRef.current, prev);
        return patched;
      });

      commitFixtures((prev) => {
        // Page membership and its aggregate total must advance from one REST
        // generation. Score/odds patches remain realtime, while membership
        // insert/remove waits for the bounded refresh scheduled above.
        if (membershipTransition) {
          return failCloseQuarantinedFixtures(prev);
        }
        if (operatorVisibility.action === 'resume') return failCloseQuarantinedFixtures(prev);
        return failCloseQuarantinedFixtures(
          applyListPatch(prev, stripOperatorVisibilityRemovals(patch)),
        );
      });
      if (!recoveryReplay) {
        sendAck(patch?.room || `sportsnew:list`, sequence, emittedAt);
        measureWsPatchToRender('list', sequence);
      }
      return true;
    // page/pageSize deliberately excluded — read via pageRef/pageSizeRef above so
    // pagination never recreates this callback (see the socket-connection effect,
    // which tears down and rebuilds the whole socket whenever this identity changes).
    }, [
    listSubscriptionRef, modeRef, listSequenceRef, acceptDisplayPatch, socketGenerationRef,
    listRecoveryInFlightRef, quarantineMembershipFixtures, fixturesRef, updateDetailCacheForFixtures,
    commitDetailMarkets, commitFixtures, sendAck, listRecoveryLastRef, SportsnewService,
    fixturesRequestSeqRef, listFilterKeyRef, sessionOwnerRef, detailRealtimeLedgerRef,
    acceptListSnapshot, recoverFixtureDetailSingleFlight, failCloseQuarantinedFixtures,
    updateOperatorSuspendedFixtureIds, clearActiveDetailIfSuspended, armMembershipQuarantineReopen,
    scheduleMembershipGenerationRefresh, flashMainOddsDiff, membershipFailClosedFixturesRef,
    detailFixtureIdRef, frameDeliveryRef, listPresentationRef, listStreamRef, requestPresentationListRecovery,
  ]);

  return handleListPatch;
};
